import React, {useState, useEffect, useCallback} from 'react';
import Icon from '../components/icons/Icon';
import { useAnalytics } from '../hooks/useAnalytics';
import AtlassianAuth from '../auth/AtlassianAuth.js';
import GitLabAuth from '../auth/GitLabAuth.js';
import GitHubAuth from '../auth/GitHubAuth.js';
import BitbucketAuth from '../auth/BitbucketAuth.js';
import ProviderLinkService from '../auth/ProviderLinkService.js';
import AzureDevOpsAuth from '../auth/AzureDevOpsAuth.js';
import GoogleAuth from '../auth/GoogleAuth.js';
import McpIntegrationService from '../auth/McpIntegrationService.js';

import miaPlatformDataService from '../services/MiaPlatformDataService.js';
import miaFlowAuth from '../auth/MiaFlowAuth.js';
import config, {isConfigLoaded} from '../config.js';
import {getBffHeaders, getBeHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import '../pages/Settings.css';
import './Connectors.css';

/**
 * Connectors Component - Manages all external integrations and connections
 */
const Connectors = () => {
  const { trackIntegration, trackAction, trackError, trackFeature } = useAnalytics();
  
  const [atlassianStatus, setAtlassianStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [gitlabStatus, setGitlabStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [githubStatus, setGithubStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [azureDevOpsStatus, setAzureDevOpsStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [bitbucketStatus, setBitbucketStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [googleStatus, setGoogleStatus] = useState({
    isAuthenticated: false,
    loading: true
  });

  const [genericMcpServers, setGenericMcpServers] = useState([]);
  const [catalogMcpServers, setCatalogMcpServers] = useState([]);
  const [catalogMcpLoading, setCatalogMcpLoading] = useState(true);
  const [currentTenantId, setCurrentTenantId] = useState(
    () => miaFlowAuth.getSelectedTenant()?.tenantId ?? null
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkAllAuthStatus = async () => {
      // Wait for config to be loaded before checking any statuses
      if (!isConfigLoaded()) {
        // eslint-disable-next-line no-console
        console.log('⏳ Waiting for config to load before checking auth statuses...');
        return;
      }

      await Promise.all([
        checkAtlassianStatus(),
        checkGitLabStatus(),
        checkGitHubStatus(),
        checkAzureDevOpsStatus(),
        checkBitbucketStatus(),
        checkGoogleStatus(),
        loadGenericMcpServers(),
        loadCatalogMcpServers()
      ]);
    };

    // Check if we just returned from a provider linking flow
    const postLinkingInfo = ProviderLinkService.checkPostLinkingReturn();
    if (postLinkingInfo) {
      // eslint-disable-next-line no-console
      console.log(`✅ Returned from ${postLinkingInfo.provider} linking flow, verifying...`);
      
      // Give the server a moment to finish storing the credential
      setTimeout(async () => {
        if (postLinkingInfo.provider === 'github') {
          await checkGitHubStatus();
          const status = await ProviderLinkService.checkLinkStatus('github');
          if (status.linked) {
            // eslint-disable-next-line no-alert
            alert('✅ GitHub account successfully linked!');
            trackIntegration('github', 'connect', { success: true, method: 'provider_token_service' });
          } else {
            // eslint-disable-next-line no-alert
            alert('⚠️ GitHub linking was not completed. Please try again.');
          }
        } else if (postLinkingInfo.provider === 'gitlab') {
          await checkGitLabStatus();
          const status = await ProviderLinkService.checkLinkStatus('gitlab');
          if (status.linked) {
            // eslint-disable-next-line no-alert
            alert('✅ GitLab account successfully linked!');
            trackIntegration('gitlab', 'connect', { success: true, method: 'provider_token_service' });
          } else {
            // eslint-disable-next-line no-alert
            alert('⚠️ GitLab linking was not completed. Please try again.');
          }
        } else if (postLinkingInfo.provider === 'google') {
          await checkGoogleStatus();
          const status = await ProviderLinkService.checkLinkStatus('google');
          if (status.linked) {
            // eslint-disable-next-line no-alert
            alert('✅ Google account successfully linked!');
            trackIntegration('google', 'connect', { success: true, method: 'provider_token_service' });
          } else {
            // eslint-disable-next-line no-alert
            alert('⚠️ Google linking was not completed. Please try again.');
          }
        }
      }, 1000);
    }

    // Check for GitHub OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const isGitHubCallback = window.location.pathname === '/auth/github/callback';
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const storedState = localStorage.getItem('github_oauth_state');
    
    if (isGitHubCallback && code && state && storedState === state) {
      // eslint-disable-next-line no-console
      console.log('🔄 Processing GitHub OAuth callback...');
      
      GitHubAuth.handleOAuthCallback()
        .then(() => {
          // eslint-disable-next-line no-console
          console.log('✅ GitHub OAuth callback handled successfully');
          
          // Check status to update UI
          checkGitHubStatus();
          
          // Navigate back to Connectors page
          const returnPath = sessionStorage.getItem('github_oauth_return_path') || '/settings';
          sessionStorage.removeItem('github_oauth_return_path');
          
          // Small delay to ensure state updates
          setTimeout(() => {
            if (window.location.pathname !== returnPath) {
              window.location.pathname = returnPath;
            }
          }, 500);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('❌ GitHub OAuth callback error:', error);
          // eslint-disable-next-line no-alert
          alert(`GitHub authentication failed: ${error.message}`);
          
          // Navigate back anyway
          window.location.pathname = '/settings';
        });
    }
    
    // Listen for OAuth completion from popup
    const handleOAuthMessage = (event) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'github_auth_success') {
        // eslint-disable-next-line no-console
        console.log('✅ GitHub OAuth completed in popup');
        checkGitHubStatus();
      } else if (event.data.type === 'github_auth_error') {
        // eslint-disable-next-line no-console
        console.error('❌ GitHub OAuth error from popup:', event.data.error);
        // eslint-disable-next-line no-alert
        alert(`GitHub authentication failed: ${event.data.error}`);
        checkGitHubStatus();
      }
    };
    
    window.addEventListener('message', handleOAuthMessage);

    checkAllAuthStatus();

    let unsubscribe;
    try {
      unsubscribe = McpIntegrationService.onStatusChange((status, data) => {
        if (status === 'initialized' || status === 'error' || status === 'auth_required') {
          // Only check status if config is loaded
          if (isConfigLoaded()) {
            checkAtlassianStatus();
          }
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to subscribe to MCP status changes:', error);
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      window.removeEventListener('message', handleOAuthMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAtlassianStatus = async () => {
    try {
      const status = await ProviderLinkService.checkLinkStatus('atlassian');

      setAtlassianStatus({
        isAuthenticated: status.linked,
        loading: false,
        user: status.user || null,
      });

      console.log('🔍 Atlassian status check:', status);
    } catch (error) {
      console.error('❌ Error checking Atlassian status:', error);
      setAtlassianStatus({ isAuthenticated: false, loading: false });
    }
  };


  const checkGitLabStatus = useCallback(async () => {
    try {
      const status = await ProviderLinkService.checkLinkStatus('gitlab');

      setGitlabStatus({
        isAuthenticated: status.linked,
        loading: false,
        user: status.user || null,
      });

      window.dispatchEvent(new CustomEvent('gitConnectionChanged', { detail: { provider: 'gitlab', connected: status.linked } }));
      console.log('🔍 GitLab status check:', status);
    } catch (error) {
      console.error('❌ Error checking GitLab status:', error);
      setGitlabStatus({ isAuthenticated: false, loading: false });
    }
  }, []);

  const checkGitHubStatus = async () => {
    try {
      const status = await ProviderLinkService.checkLinkStatus('github');

      setGithubStatus({
        isAuthenticated: status.linked,
        loading: false,
        user: status.user || null,
        oauthAvailable: false // OAuth is no longer used
      });

      window.dispatchEvent(new CustomEvent('gitConnectionChanged', { detail: { provider: 'github', connected: status.linked } }));
      console.log('🔍 GitHub status check:', status);
    } catch (error) {
      console.error('❌ Error checking GitHub status:', error);
      setGithubStatus({ isAuthenticated: false, loading: false });
    }
  };

  const checkAzureDevOpsStatus = async () => {
    try {
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('azure-devops');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status:', error.message);
      }

      const authenticated = linkStatus?.linked || await AzureDevOpsAuth.isAuthenticated();
      
      setAzureDevOpsStatus({
        isAuthenticated: authenticated,
        loading: false
      });
      window.dispatchEvent(new CustomEvent('gitConnectionChanged', { detail: { provider: 'azure-devops', connected: authenticated } }));
    } catch (error) {
      setAzureDevOpsStatus({
        isAuthenticated: false,
        loading: false
      });
    }
  };

  const checkBitbucketStatus = async () => {
    try {
      const status = await ProviderLinkService.checkLinkStatus('bitbucket');
      setBitbucketStatus({
        isAuthenticated: status.linked,
        loading: false,
        user: status.user || null,
      });
      window.dispatchEvent(new CustomEvent('gitConnectionChanged', { detail: { provider: 'bitbucket', connected: status.linked } }));
      // eslint-disable-next-line no-console
      console.log('🔍 Bitbucket status check:', status);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error checking Bitbucket status:', error);
      setBitbucketStatus({ isAuthenticated: false, loading: false });
    }
  };



  const loadGenericMcpServers = async () => {
    setGenericMcpServers([]);
  };

  const loadCatalogMcpServers = useCallback(async () => {
    const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
    setCurrentTenantId(tenantId ?? null);
    if (!tenantId) {
      setCatalogMcpServers([]);
      setCatalogMcpLoading(false);
      return;
    }
    try {
      setCatalogMcpLoading(true);
      const baseUrl = config.bffUrl || window.location.origin;
      const params = new URLSearchParams({ tenantId, page: '1', perPage: '100' });
      const response = await getAuthorizedFetch()(`${baseUrl}/api/catalog/ai-mcp-servers?${params.toString()}`, {
        headers: getBffHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch catalog MCP servers');
      const result = await response.json();
      setCatalogMcpServers(result.success ? (result.data?.items || []) : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load catalog MCP servers:', error);
      setCatalogMcpServers([]);
    } finally {
      setCatalogMcpLoading(false);
    }
  }, []);

  const handleAtlassianConnect = async () => {
    try {
      setAtlassianStatus(prev => ({...prev, loading: true}));

      // Step 1: Check whether Atlassian is already connected
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('atlassian');
        // eslint-disable-next-line no-console
        console.log('🔍 Atlassian link status:', linkStatus);
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status, continuing with the standard flow:', linkCheckError.message);
      }

      // Step 2: If not connected, start the linking flow
      if (linkStatus && !linkStatus.linked) {
        // eslint-disable-next-line no-console
        console.log('🔗 Atlassian not connected, starting the linking flow...');

        const shouldLink = window.confirm(
          'To use Atlassian features seamlessly, you need to link your Atlassian account.\n\n' +
          'Click OK to link your Atlassian account now. You\'ll be redirected to Atlassian to authorize the connection.'
        );

        if (!shouldLink) {
          // eslint-disable-next-line no-console
          console.log('❌ User declined Atlassian account linking');
          setAtlassianStatus(prev => ({...prev, loading: false}));
          return;
        }
        // Redirect back to this same page after linking (clean URL without query params)
        const redirectUri = window.location.origin + window.location.pathname;

        // eslint-disable-next-line no-console
        console.log('🔗 Starting the Atlassian linking flow...');
        console.log('   Redirect URI:', redirectUri);

        // This will redirect the user and not return
        await ProviderLinkService.redirectToLinking('atlassian', redirectUri);

        return; // This won't actually execute due to redirect
      }

      // Step 3: Already connected, or the check was unavailable: update status directly
      setAtlassianStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
        user: linkStatus?.user || null,
      });

      // eslint-disable-next-line no-console
      console.log('✅ Atlassian account is already linked');
      trackIntegration('atlassian', 'connect', { success: true, method: 'provider_token_service' });

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Atlassian authentication error:', error);
      trackIntegration('atlassian', 'connect', { success: false, error: error.message });
      // eslint-disable-next-line no-alert
      alert(`Atlassian authentication failed: ${error.message}`);
      setAtlassianStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleAtlassianDisconnect = async () => {
    try {
      await ProviderLinkService.unlinkProvider('atlassian');
      await checkAtlassianStatus();
      // eslint-disable-next-line no-console
      console.log('✅ Atlassian disconnected successfully');
      trackIntegration('atlassian', 'disconnect', { success: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Atlassian disconnect error:', error);
    }
  };



  const handleGitLabConnect = async () => {
    try {
      setGitlabStatus(prev => ({...prev, loading: true}));
      
      // Step 1: Check whether GitLab is already connected
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('gitlab');
        // eslint-disable-next-line no-console
        console.log('🔍 GitLab link status:', linkStatus);
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status, continuing with the standard flow:', linkCheckError.message);
      }

      // Step 2: If not connected, start the linking flow
      if (linkStatus && !linkStatus.linked) {
        // eslint-disable-next-line no-console
        console.log('🔗 GitLab not connected, starting the linking flow...');
        
        const shouldLink = window.confirm(
          'To use GitLab features seamlessly, you need to link your GitLab account.\n\n' +
          'Click OK to link your GitLab account now. You\'ll be redirected to GitLab to authorize the connection.'
        );

        if (!shouldLink) {
          // eslint-disable-next-line no-console
          console.log('❌ User declined GitLab account linking');
          setGitlabStatus(prev => ({...prev, loading: false}));
          return;
        }
        // Redirect back to this same page after linking (clean URL without query params)
        const redirectUri = window.location.origin + window.location.pathname;

        // eslint-disable-next-line no-console
        console.log('🔗 Starting the GitLab linking flow...');
        console.log('   Redirect URI:', redirectUri);
        
        // This will redirect the user and not return
        await ProviderLinkService.redirectToLinking('gitlab', redirectUri);
        
        return; // This won't actually execute due to redirect
      }

      // Step 3: Already connected, or the check was unavailable: update status directly
      setGitlabStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
        user: linkStatus?.user || null,
      });
      
      // eslint-disable-next-line no-console
      console.log('✅ GitLab account is already linked');
      trackIntegration('gitlab', 'connect', { success: true, method: 'provider_token_service' });
      trackFeature('gitlab_integration');

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ GitLab authentication error:', error);
      trackIntegration('gitlab', 'connect', { success: false, error: error.message });
      // eslint-disable-next-line no-alert
      alert(`GitLab authentication failed: ${error.message}`);
      setGitlabStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleGitLabDisconnect = async () => {
    try {
      await ProviderLinkService.unlinkProvider('gitlab');
      await checkGitLabStatus();
      // eslint-disable-next-line no-console
      console.log('✅ GitLab disconnected successfully');
      trackIntegration('gitlab', 'disconnect', { success: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ GitLab disconnect error:', error);
    }
  };

  const handleGitHubConnect = async () => {
    try {
      setGithubStatus(prev => ({...prev, loading: true}));
      
      // Step 1: Check whether GitHub is already connected
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('github');
        // eslint-disable-next-line no-console
        console.log('🔍 GitHub link status:', linkStatus);
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status, continuing with the standard flow:', linkCheckError.message);
      }

      // Step 2: If not connected, start the linking flow
      if (linkStatus && !linkStatus.linked) {
        // eslint-disable-next-line no-console
        console.log('🔗 GitHub not connected, starting the linking flow...');
        
        const shouldLink = window.confirm(
          'To use GitHub features seamlessly, you need to link your GitHub account.\n\n' +
          'Click OK to link your GitHub account now. You\'ll be redirected to GitHub to authorize the connection.'
        );

        if (!shouldLink) {
          // eslint-disable-next-line no-console
          console.log('❌ User declined GitHub account linking');
          setGithubStatus(prev => ({...prev, loading: false}));
          return;
        }
        // Redirect back to this same page after linking (clean URL without query params)
        const redirectUri = window.location.origin + window.location.pathname;

        // eslint-disable-next-line no-console
        console.log('🔗 Starting the GitHub linking flow...');
        console.log('   Redirect URI:', redirectUri);
        
        // This will redirect the user and not return
        await ProviderLinkService.redirectToLinking('github', redirectUri);
        
        return; // This won't actually execute due to redirect
      }

      // Step 3: Already connected, or the check was unavailable: update status directly
      setGithubStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
        user: linkStatus?.user || null,
      });
      
      // eslint-disable-next-line no-console
      console.log('✅ GitHub account is already linked');
      trackIntegration('github', 'connect', { success: true, method: 'provider_token_service' });

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ GitHub authentication error:', error);
      trackIntegration('github', 'connect', { success: false, error: error.message });
      // eslint-disable-next-line no-alert
      alert(`GitHub authentication failed: ${error.message}`);
      setGithubStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleGitHubDisconnect = async () => {
    try {
      await GitHubAuth.logout();
      await checkGitHubStatus();
      // eslint-disable-next-line no-console
      console.log('✅ GitHub disconnected successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ GitHub disconnect error:', error);
    }
  };

  const handleAzureDevOpsConnect = async () => {
    try {
      setAzureDevOpsStatus(prev => ({...prev, loading: true}));
      
      // Check whether Azure DevOps is already connected
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('azure-devops');
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status:', linkCheckError.message);
      }

      // If not connected, start the linking flow
      if (linkStatus && !linkStatus.linked) {
        const redirectUri = window.location.origin + window.location.pathname;
        await ProviderLinkService.redirectToLinking('azure-devops', redirectUri);
        return;
      }

      // Linked: the BFF resolves the token per request from provider-token-service,
      // so there is no session to prime here.
      setAzureDevOpsStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Azure DevOps connection error:', error);
      setAzureDevOpsStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleAzureDevOpsDisconnect = async () => {
    try {
      setAzureDevOpsStatus(prev => ({...prev, loading: true}));
      
      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('azure-devops');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status:', error.message);
      }

      if (linkStatus?.linked) {
        await ProviderLinkService.unlinkProvider('azure-devops');
      }

      await AzureDevOpsAuth.logout();
      setAzureDevOpsStatus({isAuthenticated: false, loading: false});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Azure DevOps disconnection error:', error);
      setAzureDevOpsStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleBitbucketConnect = async () => {
    try {
      setBitbucketStatus(prev => ({...prev, loading: true}));

      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('bitbucket');
        // eslint-disable-next-line no-console
        console.log('🔍 Bitbucket link status:', linkStatus);
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status, continuing with the standard flow:', linkCheckError.message);
      }

      if (linkStatus && !linkStatus.linked) {
        // eslint-disable-next-line no-console
        console.log('🔗 Bitbucket not connected, starting the linking flow...');

        // eslint-disable-next-line no-alert
        const shouldLink = window.confirm(
          'To use Bitbucket features seamlessly, you need to link your Bitbucket account.\n\n' +
          'Click OK to link your Bitbucket account now. You\'ll be redirected to Bitbucket to authorize the connection.'
        );

        if (!shouldLink) {
          // eslint-disable-next-line no-console
          console.log('❌ User declined Bitbucket account linking');
          setBitbucketStatus(prev => ({...prev, loading: false}));
          return;
        }

        const redirectUri = window.location.origin + window.location.pathname;
        // eslint-disable-next-line no-console
        console.log('🔗 Starting the Bitbucket linking flow...');
        console.log('   Redirect URI:', redirectUri);

        await ProviderLinkService.redirectToLinking('bitbucket', redirectUri);
        return;
      }

      setBitbucketStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
        user: linkStatus?.user || null,
      });
      // eslint-disable-next-line no-console
      console.log('✅ Bitbucket account is already linked');
      trackIntegration('bitbucket', 'connect', { success: true, method: 'provider_token_service' });

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Bitbucket authentication error:', error);
      trackIntegration('bitbucket', 'connect', { success: false, error: error.message });
      // eslint-disable-next-line no-alert
      alert(`Bitbucket authentication failed: ${error.message}`);
      setBitbucketStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleBitbucketDisconnect = async () => {
    try {
      await BitbucketAuth.logout();
      await checkBitbucketStatus();
      // eslint-disable-next-line no-console
      console.log('✅ Bitbucket disconnected successfully');
      trackIntegration('bitbucket', 'disconnect', { success: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Bitbucket disconnect error:', error);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const status = await ProviderLinkService.checkLinkStatus('google');
      setGoogleStatus({
        isAuthenticated: status.linked,
        loading: false,
        user: status.user || null,
      });
      // eslint-disable-next-line no-console
      console.log('🔍 Google status check:', status);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error checking Google status:', error);
      setGoogleStatus({ isAuthenticated: false, loading: false });
    }
  };

  const handleGoogleConnect = async () => {
    try {
      setGoogleStatus(prev => ({...prev, loading: true}));

      let linkStatus;
      try {
        linkStatus = await ProviderLinkService.checkLinkStatus('google');
        // eslint-disable-next-line no-console
        console.log('🔍 Google link status:', linkStatus);
      } catch (linkCheckError) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Could not check link status, continuing with the standard flow:', linkCheckError.message);
      }

      if (linkStatus && !linkStatus.linked) {
        // eslint-disable-next-line no-console
        console.log('🔗 Google not connected, starting the linking flow...');

        // eslint-disable-next-line no-alert
        const shouldLink = window.confirm(
          'To use Google features seamlessly, you need to link your Google account.\n\n' +
          'Click OK to link your Google account now. You\'ll be redirected to Google to authorize the connection.'
        );

        if (!shouldLink) {
          // eslint-disable-next-line no-console
          console.log('❌ User declined Google account linking');
          setGoogleStatus(prev => ({...prev, loading: false}));
          return;
        }

        const redirectUri = window.location.origin + window.location.pathname;
        // eslint-disable-next-line no-console
        console.log('🔗 Starting the Google linking flow...');
        console.log('   Redirect URI:', redirectUri);

        await ProviderLinkService.redirectToLinking('google', redirectUri);
        return;
      }

      setGoogleStatus({
        isAuthenticated: linkStatus?.linked || false,
        loading: false,
        user: linkStatus?.user || null,
      });
      // eslint-disable-next-line no-console
      console.log('✅ Google account is already linked');
      trackIntegration('google', 'connect', { success: true, method: 'provider_token_service' });

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Google authentication error:', error);
      trackIntegration('google', 'connect', { success: false, error: error.message });
      // eslint-disable-next-line no-alert
      alert(`Google authentication failed: ${error.message}`);
      setGoogleStatus(prev => ({...prev, loading: false}));
    }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await GoogleAuth.logout();
      await checkGoogleStatus();
      // eslint-disable-next-line no-console
      console.log('✅ Google disconnected successfully');
      trackIntegration('google', 'disconnect', { success: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Google disconnect error:', error);
    }
  };

  const AI_FOUNDRY_BASE_URL = config.aiFoundryBaseUrl ? `${config.aiFoundryBaseUrl}/website/mcp-servers` : null;

  const handleGenericMcpConnect = async (_serverId) => {};
  const handleGenericMcpDisconnect = async (_serverId) => {};

  const handleEditServer = (server) => {
    if (!AI_FOUNDRY_BASE_URL) return;
    window.open(`${AI_FOUNDRY_BASE_URL}/${server.itemId || server.id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="connectors-page">
      <div className="connectors-content">
      {/* Hero Section */}
      <div className="settings-hero">
        <div className="settings-hero-icon"><Icon name="settings" size={48} /></div>
        <h2 className="settings-hero-title">{'One conversation, many apps'}</h2>
        <p className="settings-hero-description">
          {'However your conversations evolve, Mia Flow can automatically pick a connected app – or multiple apps – to help respond to you.'}
        </p>
      </div>

      {/* Section 1 — Connected by default: always-on platform services, not user-configurable */}
      <section className="settings-section">
        <h3 className="settings-section-title">{'Connected by default'}</h3>
        <div className="connection-cards">
          {/* Console Card */}
          <div className="connection-card" key="console">
            <div className="connection-card-header">
              <div className="connection-card-icon mia-platform">
                <Icon glow={false} name="mia-platform" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input checked disabled type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Console'}</h4>
              <p className="connection-card-description">
                {'Centralized management of projects, environments, and configurations.'}
              </p>
              <div className="connection-card-status">
                <span className="status-connected">{'✓ Connected'}</span>
              </div>
            </div>
          </div>

          {/* Catalog Card */}
          <div className="connection-card" key="catalog">
            <div className="connection-card-header">
              <div className="connection-card-icon mia-platform">
                <Icon glow={false} name="mia-platform" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input checked disabled type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Catalog'}</h4>
              <p className="connection-card-description">
                {'Catalog of the services and components available on the platform.'}
              </p>
              <div className="connection-card-status">
                <span className="status-connected">{'✓ Connected'}</span>
              </div>
            </div>
          </div>

          {/* Data Catalog Card */}
          <div className="connection-card" key="data-catalog">
            <div className="connection-card-header">
              <div className="connection-card-icon mia-platform">
                <Icon glow={false} name="mia-platform" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input checked disabled type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Data Catalog'}</h4>
              <p className="connection-card-description">
                {'Inventory and search of the datasets available within your project.'}
              </p>
              <div className="connection-card-status">
                <span className="status-connected">{'✓ Connected'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Available connections: user-configurable integrations */}
      <section className="settings-section">
        <h3 className="settings-section-title">{'Available connections'}</h3>
        <div className="connection-cards">
          {/* Atlassian Card */}
          <div className="connection-card" key="atlassian">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="atlassian" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={atlassianStatus.isAuthenticated}
                    disabled={atlassianStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAtlassianConnect();
                      } else {
                        handleAtlassianDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Atlassian'}</h4>
              <p className="connection-card-subtitle">{'@Atlassian'}</p>
              <p className="connection-card-description">
                {'Import code from public or private repositories, and ask questions about it.'}
              </p>

              <div className="connection-card-status">
                {atlassianStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* GitLab Card */}
          <div className="connection-card" key="gitlab">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="gitlab" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={gitlabStatus.isAuthenticated}
                    disabled={gitlabStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleGitLabConnect();
                      } else {
                        handleGitLabDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'GitLab'}</h4>
              <p className="connection-card-subtitle">{'@GitLab'}</p>
              <p className="connection-card-description">
                {'Access your GitLab projects, issues, merge requests, and CI/CD pipelines.'}
              </p>

              <div className="connection-card-status">
                {gitlabStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* GitHub Card */}
          <div className="connection-card" key="github">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="github" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={githubStatus.isAuthenticated}
                    disabled={githubStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleGitHubConnect();
                      } else {
                        handleGitHubDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'GitHub'}</h4>
              <p className="connection-card-subtitle">{'@GitHub'}</p>
              <p className="connection-card-description">
                {'Access your GitHub repositories, issues, pull requests, and workflows.'}
              </p>

              <div className="connection-card-status">
                {githubStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Azure DevOps Card */}
          <div className="connection-card" key="azure-devops">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="azure-devops" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={azureDevOpsStatus.isAuthenticated}
                    disabled={azureDevOpsStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAzureDevOpsConnect();
                      } else {
                        handleAzureDevOpsDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Azure DevOps'}</h4>
              <p className="connection-card-subtitle">{'@AzureDevOps'}</p>
              <p className="connection-card-description">
                {'Connect to Azure DevOps for work items, repos, and pipelines'}
              </p>

              <div className="connection-card-status">
                {azureDevOpsStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Bitbucket Card */}
          <div className="connection-card" key="bitbucket">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="bitbucket" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={bitbucketStatus.isAuthenticated}
                    disabled={bitbucketStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleBitbucketConnect();
                      } else {
                        handleBitbucketDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Bitbucket'}</h4>
              <p className="connection-card-subtitle">{'@Bitbucket'}</p>
              <p className="connection-card-description">
                {'Access your Bitbucket repositories, pull requests, and code reviews.'}
              </p>

              <div className="connection-card-status">
                {bitbucketStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Google Card */}
          <div className="connection-card" key="google">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="google" size={32} />
              </div>
              <div className="connection-card-toggle">
                <label className="toggle-switch">
                  <input
                    checked={googleStatus.isAuthenticated}
                    disabled={googleStatus.loading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleGoogleConnect();
                      } else {
                        handleGoogleDisconnect();
                      }
                    }}
                    type="checkbox"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Google'}</h4>
              <p className="connection-card-subtitle">{'@Google'}</p>
              <p className="connection-card-description">
                {'Access your Google Drive documents, spreadsheets, and files.'}
              </p>

              <div className="connection-card-status">
                {googleStatus.isAuthenticated ? (
                  <span className="status-connected">{'✓ Connected'}</span>
                ) : (
                  <span className="status-disconnected">{'○ Not connected'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Catalog MCP Server Cards */}
          {catalogMcpLoading ? (
            <div className="connection-card" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px'}}>
              <span style={{color: 'var(--text-secondary, #718096)', fontSize: '0.875rem'}}>Loading MCP servers...</span>
            </div>
          ) : catalogMcpServers.map((server) => (
            <div className="connection-card" key={server.itemId || server.id}>
              <div className="connection-card-header">
                <div className="connection-card-icon">
                  <Icon glow={false} name="mcp-server" size={32} />
                </div>
              </div>
              <div className="connection-card-body">
                <h4 className="connection-card-title">{server.name}</h4>
                <p className="connection-card-subtitle">{server.type || 'streamable_http'}</p>
                <p className="connection-card-description">{server.url || server.description || ''}</p>
                <div className="connection-card-status">
                  <span className="status-connected" key="status">{'✓ Registered in Catalog'}</span>
                  {server.tool_name_prefix && (
                    <span className="status-tools" key="prefix">{`prefix: ${server.tool_name_prefix}`}</span>
                  )}
                </div>
              </div>
              <button
                className="connection-card-learn-more"
                disabled={!AI_FOUNDRY_BASE_URL}
                onClick={() => handleEditServer(server)}
                type="button"
              >
                {'Edit Configuration'}
              </button>
            </div>
          ))}

          {/* Add New MCP Server Card — opens AI Foundry */}
          <div
            className="connection-card add-card"
            key="add-new"
            onClick={() => AI_FOUNDRY_BASE_URL && window.open(`${AI_FOUNDRY_BASE_URL}/create`, '_blank', 'noopener,noreferrer')}
            onKeyDown={(e) => e.key === 'Enter' && AI_FOUNDRY_BASE_URL && window.open(`${AI_FOUNDRY_BASE_URL}/create`, '_blank', 'noopener,noreferrer')}
            role="button"
            tabIndex={0}
          >
            <div className="add-card-content">
              <div className="add-card-icon">
                <span className="add-icon-large">{'+'}</span>
              </div>
              <h4 className="add-card-title">{'Add new MCP Server'}</h4>
              <p className="add-card-description">
                {'Register a new MCP Server via AI Foundry'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — Coming soon: integrations not yet available, informational only */}
      <section className="settings-section">
        <h3 className="settings-section-title">{'Coming soon'}</h3>
        <div className="connection-cards">
          {/* Grafana Card */}
          <div className="connection-card connection-card-coming-soon" key="grafana">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="grafana" size={32} />
              </div>
            </div>
            <div className="connection-card-body">
              <h4 className="connection-card-title">{'Grafana'}</h4>
              <p className="connection-card-description">
                {'Monitor and visualize your system metrics, dashboards, and alerts.'}
              </p>
              <div className="connection-card-status">
                <span className="status-coming-soon">{'Coming soon'}</span>
              </div>
            </div>
          </div>

          {/* P4SAMD Card */}
          <div className="connection-card connection-card-coming-soon" key="p4samd">
            <div className="connection-card-header">
              <div className="connection-card-icon">
                <Icon glow={false} name="p4samd" size={32} />
              </div>
            </div>
            <div className="connection-card-body">
              <h4 className="connection-card-title">{'P4SAMD'}</h4>
              <p className="connection-card-description">
                {'Integration currently in development.'}
              </p>
              <div className="connection-card-status">
                <span className="status-coming-soon">{'Coming soon'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Notice */}
      <div className="settings-privacy">
        <h3 className="settings-privacy-title">{'How Mia Flow respects your privacy:'}</h3>
        <p className="settings-privacy-text">
          {'Your content is not used to improve Mia Flow. You can turn off integrations from the Settings page. '}
          <button className="privacy-link" disabled type="button">
            {'Learn how your content is used'}
          </button>
          {'. Public repositories are not subject to this permission.'}
        </p>
      </div>


      </div>
    </div>
  );
};

export default Connectors;
